import { useEffect, useMemo, useState } from "react";
import {
  BookOpen,
  Check,
  KeyRound,
  Loader2,
  Lock,
  Plus,
  ShieldCheck,
  Webhook,
} from "lucide-react";
import { useHive } from "@/store/useHive";
import { useWebhooks } from "@/store/webhookStore";
import { useAuth } from "@/auth/AuthProvider";
import { APPS } from "@/data/apps";
import { Modal } from "@/components/ui/Modal";
import { CodeBlock, CodeLine, curlFor, DIRECTION_META, SCOPE_META } from "./webhookUi";
import type { WebhookDirection, WebhookScope } from "@/types";

const accent = APPS.integrations.accent;

/** Node snippet consumers paste to verify outbound signatures. */
const VERIFY_SNIPPET = `// Express example — verify X-Hive-Signature on your endpoint
import crypto from "node:crypto";

app.post("/hive-webhook", express.raw({ type: "*/*" }), (req, res) => {
  const ts = req.headers["x-hive-timestamp"];
  const sig = req.headers["x-hive-signature"]; // "v1=<hex>"
  const expected = "v1=" + crypto
    .createHmac("sha256", process.env.HIVE_WEBHOOK_SECRET)
    .update(\`\${ts}.\${req.body}\`)
    .digest("hex");
  if (!crypto.timingSafeEqual(Buffer.from(sig), Buffer.from(expected)))
    return res.status(401).end();
  const { event, data } = JSON.parse(req.body);
  // handle event…
  res.json({ ok: true });
});`;

export function WebhookCreateModal({
  open,
  onClose,
  presetDirection,
  presetIntegrationId,
  onOpenGuide,
}: {
  open: boolean;
  onClose: () => void;
  presetDirection?: WebhookDirection;
  presetIntegrationId?: string | null;
  onOpenGuide?: () => void;
}) {
  const create = useWebhooks((s) => s.create);
  const eventDefs = useWebhooks((s) => s.eventDefs);
  const loadEvents = useWebhooks((s) => s.loadEvents);
  const workspaces = useHive((s) => s.codeWorkspaces);
  const integrations = useHive((s) => s.integrations);
  const { me } = useAuth();
  const isAdmin = me?.role === "admin" || me?.role === "owner";

  const [direction, setDirection] = useState<WebhookDirection>(presetDirection ?? "inbound");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [scope, setScope] = useState<WebhookScope>("user");
  const [workspaceId, setWorkspaceId] = useState("");
  const [integrationId, setIntegrationId] = useState(presetIntegrationId ?? "");
  const [url, setUrl] = useState("");
  const [allEvents, setAllEvents] = useState(true);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      void loadEvents();
      setDirection(presetDirection ?? "inbound");
      setIntegrationId(presetIntegrationId ?? "");
      setName("");
      setDescription("");
      setScope("user");
      setWorkspaceId("");
      setUrl("");
      setAllEvents(true);
      setPicked(new Set());
      setProblem(null);
    }
  }, [open, presetDirection, presetIntegrationId, loadEvents]);

  const groups = useMemo(() => {
    const byGroup = new Map<string, typeof eventDefs>();
    for (const def of eventDefs) {
      if (!byGroup.has(def.group)) byGroup.set(def.group, []);
      byGroup.get(def.group)!.push(def);
    }
    return [...byGroup.entries()];
  }, [eventDefs]);

  const validationError = (): string | null => {
    if (!name.trim()) return "Give the webhook a name";
    if (scope === "workspace" && !workspaceId) return "Pick the workspace this team webhook belongs to";
    if (direction === "outbound") {
      if (!url.trim()) return "Outbound webhooks need a delivery URL";
      if (!/^https?:\/\//i.test(url.trim())) return "The URL must start with http:// or https://";
      if (!allEvents && picked.size === 0) return "Subscribe to at least one event (or all)";
    }
    return null;
  };

  const submit = async () => {
    const invalid = validationError();
    if (invalid) {
      setProblem(invalid);
      return;
    }
    setProblem(null);
    setBusy(true);
    const created = await create({
      direction,
      name: name.trim(),
      description: description.trim() || undefined,
      scope,
      workspaceId: scope === "workspace" || workspaceId ? workspaceId || undefined : undefined,
      integrationId: integrationId || undefined,
      url: direction === "outbound" ? url.trim() : undefined,
      events: direction === "outbound" ? (allEvents ? ["*"] : [...picked]) : undefined,
    });
    setBusy(false);
    if (created) onClose(); // the one-time reveal modal takes over
  };

  const activeWorkspaces = workspaces.filter((w) => !w.archived);

  return (
    <Modal
      open={open}
      onClose={onClose}
      accent={accent}
      icon={<Webhook size={18} />}
      title="New webhook"
      subtitle={DIRECTION_META[direction].blurb}
      width={640}
    >
      <div className="space-y-4">
        {/* direction */}
        <div className="grid grid-cols-2 gap-2">
          {(Object.keys(DIRECTION_META) as WebhookDirection[]).map((d) => {
            const meta = DIRECTION_META[d];
            const active = direction === d;
            return (
              <button
                key={d}
                onClick={() => setDirection(d)}
                className="flex items-start gap-2.5 rounded-xl border p-3 text-left transition"
                style={{
                  borderColor: active ? meta.color : "hsl(var(--border))",
                  background: active ? `${meta.color}14` : "transparent",
                }}
              >
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg" style={{ background: `${meta.color}1f`, color: meta.color }}>
                  <meta.icon size={15} />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-app">{meta.label}</span>
                  <span className="mt-0.5 block text-[11px] leading-snug text-app-2">{meta.blurb}</span>
                </span>
              </button>
            );
          })}
        </div>

        {/* name + description */}
        <div className="grid grid-cols-2 gap-2.5">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-app-2">Name</label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={direction === "inbound" ? "GitHub push events" : "Notify #eng-alerts service"}
              className="mt-1.5 w-full rounded-lg border border-app bg-surface px-3 py-2 text-sm text-app outline-none transition focus:border-app-strong placeholder:text-app-2"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-app-2">Description (optional)</label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this for?"
              className="mt-1.5 w-full rounded-lg border border-app bg-surface px-3 py-2 text-sm text-app outline-none transition focus:border-app-strong placeholder:text-app-2"
            />
          </div>
        </div>

        {/* scope */}
        <div>
          <label className="text-[11px] uppercase tracking-wider text-app-2">Level</label>
          <div className="mt-1.5 grid grid-cols-3 gap-2">
            {(Object.keys(SCOPE_META) as WebhookScope[]).map((sc) => {
              const meta = SCOPE_META[sc];
              const disabled = sc === "org" && !isAdmin;
              const active = scope === sc;
              return (
                <button
                  key={sc}
                  disabled={disabled}
                  onClick={() => setScope(sc)}
                  title={disabled ? "Requires an org admin" : meta.blurb}
                  className="flex flex-col items-start gap-1 rounded-xl border p-2.5 text-left transition disabled:cursor-not-allowed disabled:opacity-45"
                  style={{
                    borderColor: active ? accent : "hsl(var(--border))",
                    background: active ? `${accent}12` : "transparent",
                  }}
                >
                  <span className="flex items-center gap-1.5 text-xs font-semibold text-app">
                    <meta.icon size={12} /> {meta.label}
                    {sc === "org" && !isAdmin && <Lock size={10} className="text-violet-500" />}
                  </span>
                  <span className="text-[10px] leading-snug text-app-2">{meta.blurb}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* workspace + integration link */}
        <div className="grid grid-cols-2 gap-2.5">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-app-2">
              Workspace {scope === "workspace" ? "(the team)" : "(optional)"}
            </label>
            <select
              value={workspaceId}
              onChange={(e) => setWorkspaceId(e.target.value)}
              className="mt-1.5 w-full rounded-lg border border-app bg-surface px-2.5 py-2 text-sm text-app outline-none focus:border-app-strong"
            >
              <option value="">{scope === "workspace" ? "Pick a workspace…" : "None"}</option>
              {activeWorkspaces.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name} · {w.repo}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-app-2">Linked integration (optional)</label>
            <select
              value={integrationId}
              onChange={(e) => setIntegrationId(e.target.value)}
              className="mt-1.5 w-full rounded-lg border border-app bg-surface px-2.5 py-2 text-sm text-app outline-none focus:border-app-strong"
            >
              <option value="">None</option>
              {integrations.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
            </select>
            {direction === "inbound" && (
              <p className="mt-1 text-[10px] leading-snug text-app-2">
                Inbound events will light up on the linked integration's card.
              </p>
            )}
          </div>
        </div>

        {/* outbound: url + events */}
        {direction === "outbound" && (
          <>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-app-2">Delivery URL</label>
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/hooks/hive"
                className="mt-1.5 w-full rounded-lg border border-app bg-surface px-3 py-2 font-mono text-xs text-app outline-none transition focus:border-app-strong placeholder:text-app-2"
              />
              <p className="mt-1 flex items-center gap-1 text-[10px] text-app-2">
                <ShieldCheck size={10} className="text-emerald-500" />
                Every delivery is signed with X-Hive-Signature so your endpoint can verify it came from Hive.
              </p>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-[11px] uppercase tracking-wider text-app-2">Events</label>
                <button
                  onClick={() => setAllEvents((v) => !v)}
                  className="flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium transition"
                  style={{ background: allEvents ? `${accent}1f` : "transparent", color: allEvents ? accent : "var(--text-2)" }}
                >
                  <Check size={11} style={{ opacity: allEvents ? 1 : 0.3 }} /> All events (*)
                </button>
              </div>
              {!allEvents && (
                <div className="mt-2 max-h-44 space-y-3 overflow-y-auto rounded-xl border border-app p-3 no-scrollbar">
                  {groups.map(([group, defs]) => (
                    <div key={group}>
                      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-app-2">{group}</div>
                      <div className="grid grid-cols-2 gap-1">
                        {defs.map((def) => {
                          const on = picked.has(def.event);
                          return (
                            <button
                              key={def.event}
                              onClick={() =>
                                setPicked((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(def.event)) next.delete(def.event);
                                  else next.add(def.event);
                                  return next;
                                })
                              }
                              title={def.description}
                              className="flex items-center gap-1.5 rounded-md px-1.5 py-1 text-left font-mono text-[11px] transition"
                              style={{ background: on ? `${accent}1a` : "transparent", color: on ? accent : "var(--text-2)" }}
                            >
                              <Check size={11} style={{ opacity: on ? 1 : 0.25 }} /> {def.event}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {problem && (
          <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-500">
            {problem}
          </div>
        )}

        <div className="flex items-center gap-2">
          <button
            disabled={busy}
            onClick={() => void submit()}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2.5 text-sm font-semibold text-black transition hover:brightness-105 disabled:opacity-50"
            style={{ background: accent }}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Create webhook
          </button>
          {onOpenGuide && (
            <button
              onClick={onOpenGuide}
              className="flex items-center gap-1.5 rounded-lg border border-app px-3 py-2.5 text-xs font-medium text-app-1 transition hover:border-app-strong"
            >
              <BookOpen size={13} /> Guide
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}

/** One-time secret/URL reveal after create, rotate or reveal. */
export function WebhookRevealModal() {
  const reveal = useWebhooks((s) => s.reveal);
  const clearReveal = useWebhooks((s) => s.clearReveal);
  const hook = useWebhooks((s) => s.webhooks.find((w) => w.id === s.reveal?.webhookId));

  if (!reveal) return null;
  const inbound = Boolean(reveal.inboundUrl);
  const title =
    reveal.reason === "created" ? "Webhook ready" : reveal.reason === "rotated" ? "New secret issued" : "Endpoint URL";

  return (
    <Modal
      open
      onClose={clearReveal}
      accent={accent}
      icon={<KeyRound size={18} />}
      title={title}
      subtitle={
        reveal.reason === "revealed"
          ? "Treat this URL as a credential — anyone holding it can send events"
          : "Copy these now — the secret is stored hashed and shown only this once"
      }
      width={640}
    >
      <div className="space-y-4">
        {inbound && reveal.inboundUrl && (
          <div>
            <div className="mb-1.5 text-[11px] uppercase tracking-wider text-app-2">
              Endpoint URL — paste into the sender's webhook settings
            </div>
            <CodeLine text={reveal.inboundUrl} />
          </div>
        )}
        <div>
          <div className="mb-1.5 text-[11px] uppercase tracking-wider text-app-2">
            Signing secret {inbound ? "— optional HMAC (GitHub “secret” field)" : "— verify X-Hive-Signature with it"}
          </div>
          <CodeLine text={reveal.secret} />
        </div>
        {inbound && reveal.inboundUrl ? (
          <div>
            <div className="mb-1.5 text-[11px] uppercase tracking-wider text-app-2">Try it from your terminal</div>
            <CodeBlock text={curlFor(reveal.inboundUrl)} />
            <p className="mt-2 text-[11px] leading-relaxed text-app-2">
              The delivery appears instantly in this webhook's log{hook?.integrationId ? " and on the linked integration's card" : ""}.
              GitHub, Stripe, Slack and friends: paste the URL as the webhook target; if the provider supports a
              signing secret, paste the secret too and Hive verifies each request's HMAC.
            </p>
          </div>
        ) : (
          <div>
            <div className="mb-1.5 text-[11px] uppercase tracking-wider text-app-2">Verify deliveries on your server</div>
            <CodeBlock text={VERIFY_SNIPPET} />
          </div>
        )}
        <button
          onClick={clearReveal}
          className="w-full rounded-lg py-2.5 text-sm font-semibold text-black transition hover:brightness-105"
          style={{ background: accent }}
        >
          I've stored it safely
        </button>
      </div>
    </Modal>
  );
}
